import React from 'react';
import { Button, Label, TextInput, Textarea } from 'flowbite-react';
import { useFormik } from 'formik';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

// Validation schema
const workflowTemplateSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
});

export type WorkflowTemplateFormData = z.infer<typeof workflowTemplateSchema>;

interface WorkflowTemplateFormProps {
  onSubmit: (data: WorkflowTemplateFormData) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<WorkflowTemplateFormData>;
  isSubmitting?: boolean;
}

const WorkflowTemplateForm: React.FC<WorkflowTemplateFormProps> = ({
  onSubmit,
  onCancel,
  initialData = {},
  isSubmitting = false,
}) => {
  const formik = useFormik({
    initialValues: {
      title: initialData.title || '',
      description: initialData.description || '',
    },
    validationSchema: toFormikValidationSchema(workflowTemplateSchema),
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="title">Template Title</Label>
        <TextInput
          id="title"
          name="title"
          type="text"
          placeholder="Enter workflow template title"
          value={formik.values.title}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          color={formik.touched.title && formik.errors.title ? 'failure' : undefined}
          required
        />
        {formik.touched.title && formik.errors.title && (
          <p className="mt-1 text-sm text-red-600">{formik.errors.title}</p>
        )}
      </div>

      <div>
        <Label htmlFor="description">Description (Optional)</Label>
        <Textarea
          id="description"
          name="description"
          placeholder="Enter workflow template description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          rows={3}
          color={formik.touched.description && formik.errors.description ? 'failure' : undefined}
        />
        {formik.touched.description && formik.errors.description && (
          <p className="mt-1 text-sm text-red-600">{formik.errors.description}</p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          color="gray"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || !formik.isValid}
        >
          {isSubmitting ? 'Creating...' : 'Create Template'}
        </Button>
      </div>
    </form>
  );
};

export default WorkflowTemplateForm;
